import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface CarData {
  id: string;
  make: string;
  model: string;
  year: number;
  variant: string;
  fuelType: string | null;
  imageUrl?: string | null;
}

export interface SelectedCar {
  id: string;
  userCarId?: string;
  make: string;
  model: string;
  year: number;
  variant: string;
  iconUrl?: string | null;
  logoUrl?: string | null;
}

interface CarState {
  selectedCar: SelectedCar | null;
  isModalOpen: boolean;
  isCheckoutMode: boolean;
  onCarConfirmed: (() => void) | null;
  setSelectedCar: (
    car: SelectedCar | null,
    options?: { closeModal?: boolean },
  ) => void;
  clearSelectedCar: () => void;
  openModal: (
    mode?: "checkout" | "select",
    onConfirmed?: () => void,
  ) => void;
  closeModal: () => void;
}

export const useCarStore = create<CarState>()(
  persist(
    (set) => ({
      selectedCar: null,
      isModalOpen: false,
      isCheckoutMode: false,
      onCarConfirmed: null,

      setSelectedCar: (car, options) => {
        const shouldCloseModal = options?.closeModal ?? true;
        set((state) => ({
          selectedCar: car,
          isModalOpen: shouldCloseModal ? false : state.isModalOpen,
        }));
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("carChanged", { detail: car }));
        }
      },

      clearSelectedCar: () => {
        set({ selectedCar: null });
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("carChanged", { detail: null }));
        }
      },

      openModal: (mode = "select", onConfirmed) => {
        set({
          isModalOpen: true,
          isCheckoutMode: mode === "checkout",
          onCarConfirmed: onConfirmed ?? null,
        });
      },

      closeModal: () =>
        set({
          isModalOpen: false,
          isCheckoutMode: false,
          onCarConfirmed: null,
        }),
    }),
    {
      name: "car-storage",
      partialize: (state) => ({
        selectedCar: state.selectedCar,
      }),
    },
  ),
);

if (typeof window !== "undefined") {
  window.addEventListener("userLogout", () => {
    useCarStore.getState().clearSelectedCar();
  });
}
